import { useEffect, useState } from "react";
import { Bell, BellOff, Volume2, MonitorSmartphone } from "lucide-react";
import { toast } from "sonner";
import { Hint } from "@/components/Hint";
import { playChime, requestBrowserNotifications, setSoundEnabled, soundEnabled, unlockAudio } from "@/lib/attention";

export const AlertAttentionCard = () => {
  const [sound, setSound] = useState(soundEnabled());
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  useEffect(() => {
    const sync = () => setSound(soundEnabled());
    window.addEventListener("apx:sound-pref", sync);
    return () => window.removeEventListener("apx:sound-pref", sync);
  }, []);

  const toggle = () => { unlockAudio(); setSoundEnabled(!sound); if (!sound) playChime(true); };
  const test = () => { unlockAudio(); playChime(true); window.dispatchEvent(new CustomEvent("apx:alert-fired", { detail: { title: "Test" } })); toast("Chime played and bell flashed", { description: "This is how live alerts get your attention." }); };
  const enableBrowser = async () => {
    const res = await requestBrowserNotifications();
    setPerm(res);
    if (res === "granted") toast.success("Browser notifications enabled — alerts will pop up even in another tab.");
    else if (res === "denied") toast.error("Notifications are blocked for this site in your browser settings.");
  };

  return (
    <div className="apx-card p-5" data-testid="alert-attention-card">
      <div className="font-display font-semibold mb-1">Alert attention</div>
      <p className="text-xs text-slate-400 mb-4">How EV-IDEX gets your attention when a live price alert or signal setup fires — including when this tab is in the background.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <Hint title="Alert chime" text="A short two-tone bell plays whenever an alert fires. Browsers only allow sound after you've interacted with the page once." asChild>
          <button data-testid="toggle-alert-sound" onClick={toggle}
            className={`apx-card p-3 text-left flex items-center gap-3 border transition-colors ${sound ? "border-emerald-900/60 bg-emerald-950/20" : "border-slate-800"}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${sound ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-400"}`}>
              {sound ? <Volume2 className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            </div>
            <div>
              <div className="text-sm font-medium">Chime {sound ? "on" : "off"}</div>
              <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">Tap to {sound ? "mute" : "enable"}</div>
            </div>
          </button>
        </Hint>
        <Hint title="Bell flash" text="The notification bell glows and swings for a few seconds, and the browser tab title blinks while you're on another tab." asChild>
          <button data-testid="test-alert-chime" onClick={test} className="apx-card p-3 text-left flex items-center gap-3 border border-slate-800 hover:border-cyan-900/60">
            <div className="w-9 h-9 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center"><Bell className="w-4 h-4" /></div>
            <div>
              <div className="text-sm font-medium">Test alert</div>
              <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">Chime + bell flash</div>
            </div>
          </button>
        </Hint>
        <Hint title="Browser notifications" text="With permission, a system notification pops up when an alert fires while this tab is hidden." asChild>
          <button data-testid="enable-browser-notifications" onClick={enableBrowser} disabled={perm === "granted" || perm === "unsupported"}
            className={`apx-card p-3 text-left flex items-center gap-3 border ${perm === "granted" ? "border-emerald-900/60 bg-emerald-950/20" : "border-slate-800 hover:border-cyan-900/60"} disabled:cursor-default`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${perm === "granted" ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-400"}`}><MonitorSmartphone className="w-4 h-4" /></div>
            <div>
              <div className="text-sm font-medium">{perm === "granted" ? "Desktop pop-ups on" : perm === "denied" ? "Pop-ups blocked" : perm === "unsupported" ? "Pop-ups unsupported" : "Enable desktop pop-ups"}</div>
              <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{perm === "granted" ? "Even in another tab" : "Background tab alerts"}</div>
            </div>
          </button>
        </Hint>
      </div>
    </div>
  );
};
